import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { createStrainEntry } from "@/lib/admin-content.functions";
import { strainInputSchema } from "@/lib/admin-content.schemas";
import { StrainFields, EMPTY_STRAIN, type StrainFormValues } from "@/components/admin/StrainForm";
import { BatchFields, EMPTY_BATCH, type BatchFormValues } from "@/components/admin/BatchForm";

export const Route = createFileRoute("/_authenticated/_admin/admin/medcan/new")({
  component: NewMedCanPage,
});

const cardStyle: React.CSSProperties = {
  background: "var(--bg-surface)",
  border: "1px solid var(--border-purple)",
  borderRadius: "var(--radius-sm)",
  padding: "1.5rem",
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
};

function NewMedCanPage() {
  const navigate = useNavigate();
  const submit = useServerFn(createStrainEntry);
  const qc = useQueryClient();
  const [submitting, setSubmitting] = useState(false);
  const [strain, setStrain] = useState<StrainFormValues>(EMPTY_STRAIN);
  const [batch, setBatch] = useState<BatchFormValues>(EMPTY_BATCH);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = strainInputSchema.safeParse({ ...strain, ...batch });
    if (!parsed.success) {
      toast.error("Eingabe ungültig", { description: parsed.error.issues.map(i => i.message).join("; ") });
      return;
    }
    setSubmitting(true);
    try {
      await submit({ data: parsed.data });
      toast.success("MedCan-Eintrag angelegt");
      qc.invalidateQueries({ queryKey: ["admin-content"] });
      navigate({ to: "/admin" });
    } catch (err: any) {
      toast.error("Speichern fehlgeschlagen", { description: err?.message ?? String(err) });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ maxWidth: 900, margin: "2rem auto", padding: "0 1.5rem" }}>
      <h1 style={{ color: "var(--accent-cyan)", marginBottom: "1rem" }}>Neuer MedCan-Eintrag</h1>
      <form onSubmit={handleSubmit} style={cardStyle}>
        <h2 style={{ margin: 0, fontSize: "1rem", color: "var(--text-primary)" }}>Strain</h2>
        <StrainFields values={strain} onChange={setStrain} />
        <hr style={{ border: "none", borderTop: "1px solid var(--border-purple)" }} />
        <h2 style={{ margin: 0, fontSize: "1rem", color: "var(--text-primary)" }}>Batch / Review</h2>
        <BatchFields values={batch} onChange={setBatch} />
        <div style={{ display: "flex", gap: 8 }}>
          <button type="submit" disabled={submitting} style={{
            background: "var(--accent-cyan)", color: "#001018", border: "none", borderRadius: 6,
            padding: "0.6rem 1rem", fontWeight: 600, cursor: submitting ? "wait" : "pointer", opacity: submitting ? 0.6 : 1,
          }}>{submitting ? "Speichere…" : "Speichern"}</button>
          <button type="button" onClick={() => navigate({ to: "/admin" })} style={{
            background: "transparent", color: "var(--text-muted)", border: "1px solid var(--border-purple)",
            borderRadius: 6, padding: "0.6rem 1rem", cursor: "pointer",
          }}>Abbrechen</button>
        </div>
      </form>
    </div>
  );
}
